/**
 * The four calls that actually draw a receipt: decode it, put it on a canvas
 * at the size `src/lib/receipt-file.ts` worked out, and read the canvas back
 * as a `Blob`.
 *
 * Everything that *decides* anything — what the target size is, whether the
 * file is a kind we take, whether the result fits in what is left of the quota
 * — is next door, in pure functions a test can run without a DOM. This file is
 * the part that needs a browser, and it is deliberately as small as it can be.
 *
 * **A photo becomes a WebP, whatever it arrived as.** An iPhone HEIC, a
 * scanner's 6 MB PNG and a JPEG that has been screenshotted twice all come out
 * of `prepareReceipt` as the same thing: WebP at 0.82, long edge 2000, plus a
 * 400px WebP thumbnail. That is one content type for the server to allow, one
 * for the browser to render, and a tenth of the bytes.
 *
 * **A PDF keeps its bytes and gets a picture of its first page.** Re-encoding
 * an emailed invoice would throw away the selectable text and the only exact
 * copy of it; what it needs is not a smaller PDF, it is something to *show* in
 * a list. So the PDF is uploaded as-is and pdf.js renders page 1 to the same
 * 400px canvas the images use.
 *
 * pdf.js is imported dynamically, so the megabyte of PDF renderer is a chunk
 * the browser only ever fetches if somebody actually attaches a PDF; the app's
 * first load does not pay for it. The pinned major is 6 rather than 5 for a
 * reason worth keeping in the lockfile's comment as well as here: pdfjs-dist
 * below 6.2.108 will execute JavaScript out of a malicious PDF
 * (GHSA-hq66-cqwq-w95j), and the PDFs this function opens are files somebody
 * was *sent*. Annotations are switched off when the page is drawn, for the
 * same reason.
 *
 * **A scanned page takes the same road from halfway along.** `prepareScan`
 * starts from a frame the camera has already given us and the four corners
 * `src/lib/receipt-edges.ts` found in it, flattens the one onto the other, and
 * then hands the result to exactly the calls a picked file goes through — same
 * long edge, same 0.82, same 400px thumbnail. So a scan and a photo are the
 * same thing by the time either reaches `server/receipts.ts`, and there is no
 * second upload path to keep in step with the first.
 */

import type { Quad } from './receipt-edges'
import {
  LONG_EDGE,
  THUMBNAIL_EDGE,
  THUMBNAIL_QUALITY,
  WEBP_QUALITY,
  receiptKindOf,
  scaleToLongEdge,
  type ReceiptKind,
} from './receipt-file'

/** A file, prepared, and ready to be one multipart POST. */
export type PreparedReceipt = {
  kind: ReceiptKind
  /** What goes up as `file`: the re-encoded image, or the PDF untouched. */
  file: Blob
  contentType: string
  /** What goes up as `thumb`. `null` when the browser could not draw one. */
  thumbnail: Blob | null
  originalName: string
  /** Before and after, so the screen can say what the shrinking bought. */
  originalBytes: number
  /** `file` plus `thumbnail` — what this will cost against the quota. */
  storedBytes: number
}

/** A file we will not take, named so a screen can say which and why. */
export class UnsupportedReceipt extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UnsupportedReceipt'
  }
}

/**
 * A picked file, ready to upload.
 *
 * Nothing here throws for a *big* file: the whole point of redrawing it is
 * that a 12 MB photo becomes an acceptable one. What it refuses is a file it
 * cannot make anything of — an SVG, a zip, a PDF that will not open — because
 * those are refusals the server would make anyway and the file picker is a
 * better place to hear them.
 */
export async function prepareReceipt(file: File): Promise<PreparedReceipt> {
  const kind = receiptKindOf(file)
  if (kind === null) {
    throw new UnsupportedReceipt(
      `${file.name || 'that file'} is not a photo or a PDF, so it cannot be attached`,
    )
  }

  const originalName = file.name || (kind === 'pdf' ? 'receipt.pdf' : 'receipt.jpg')

  if (kind === 'pdf') {
    const thumbnail = await pdfThumbnail(file)
    return {
      kind,
      file,
      contentType: 'application/pdf',
      thumbnail,
      originalName,
      originalBytes: file.size,
      storedBytes: file.size + (thumbnail?.size ?? 0),
    }
  }

  const bitmap = await decode(file)
  try {
    const full = await drawTo(bitmap, LONG_EDGE, WEBP_QUALITY)
    const thumbnail = await drawTo(bitmap, THUMBNAIL_EDGE, THUMBNAIL_QUALITY)
    return {
      kind,
      file: full,
      contentType: full.type || 'image/webp',
      thumbnail,
      originalName,
      originalBytes: file.size,
      storedBytes: full.size + (thumbnail?.size ?? 0),
    }
  } finally {
    bitmap.close()
  }
}

async function decode(file: File): Promise<ImageBitmap> {
  try {
    // `imageOrientation: 'from-image'` is what stops a photo taken sideways
    // from being stored sideways: the EXIF rotation lives in the file, and a
    // canvas draws pixels rather than metadata, so the rotation has to be
    // applied at decode time or it is lost when the canvas is read back.
    return await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    throw new UnsupportedReceipt(
      `${file.name || 'that image'} could not be opened — it may be a format this browser does not read`,
    )
  }
}

/** Anything with a size that a 2d context will draw. */
type Source = ImageBitmap | AnyCanvas

/** The source, scaled to `maxEdge` and read back as WebP. */
async function drawTo(source: Source, maxEdge: number, quality: number): Promise<Blob> {
  const size = scaleToLongEdge({ width: source.width, height: source.height }, maxEdge)
  const canvas = newCanvas(size.width, size.height)

  const context = canvas.getContext('2d')
  if (!context) throw new UnsupportedReceipt('this browser cannot draw to a canvas')
  // The default is 'low' on some browsers, which turns a downscale of a photo
  // into aliased mush exactly where the small print is.
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(source, 0, 0, size.width, size.height)

  return toBlob(canvas, quality)
}

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement

function newCanvas(width: number, height: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(width, height)
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  return canvas
}

/**
 * The canvas as a WebP blob.
 *
 * Two APIs for one thing: `OffscreenCanvas` has a promise, `HTMLCanvasElement`
 * has a callback that hands back `null` when it cannot encode. A browser
 * without WebP would land in that `null`, and the honest thing then is to say
 * so rather than to upload a PNG four times the size under a WebP's name.
 */
async function toBlob(canvas: AnyCanvas, quality: number): Promise<Blob> {
  if ('convertToBlob' in canvas) {
    return canvas.convertToBlob({ type: 'image/webp', quality })
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(blob)
          : reject(new UnsupportedReceipt('this browser could not encode the image')),
      'image/webp',
      quality,
    )
  })
}

/**
 * Page 1 of a PDF, drawn at 400px on its long edge.
 *
 * A PDF that will not open is not fatal: the file itself is perfectly
 * uploadable and a missing thumbnail is a placeholder in a list, so this
 * answers `null` rather than throwing and taking a legitimate receipt down
 * with it.
 */
export async function pdfThumbnail(file: File): Promise<Blob | null> {
  try {
    const pdfjs = await import('pdfjs-dist')
    // Vite resolves this to a hashed URL at build time and to the real module
    // in dev; the worker is a separate file either way, which is the whole
    // reason pdf.js parses a document off the main thread.
    pdfjs.GlobalWorkerOptions.workerSrc = (
      await import('pdfjs-dist/build/pdf.worker.min.mjs?url')
    ).default

    const loading = pdfjs.getDocument({
      data: new Uint8Array(await file.arrayBuffer()),
    })

    try {
      const page = await (await loading.promise).getPage(1)
      const unscaled = page.getViewport({ scale: 1 })
      const size = scaleToLongEdge(
        { width: unscaled.width, height: unscaled.height },
        THUMBNAIL_EDGE,
      )
      const scale = size.width / unscaled.width
      const viewport = page.getViewport({ scale })

      const canvas = newCanvas(size.width, size.height)
      const context = canvas.getContext('2d')
      if (!context) return null
      // A PDF page is transparent where it is blank, and a transparent
      // thumbnail on a dark background is an unreadable grey rectangle.
      //
      // The one white in this codebase that is not a theme token, and it is
      // not chrome: it is the *paper*. A receipt is black ink on a white page
      // in Midnight and in Daylight alike, and tinting somebody's scanned
      // document to match their theme would be altering the record. Every
      // colour that belongs to the app is in `src/theme.css`; this belongs to
      // the file.
      context.fillStyle = '#ffffff'
      context.fillRect(0, 0, size.width, size.height)

      await page.render({
        canvas: canvas as HTMLCanvasElement,
        canvasContext: context as CanvasRenderingContext2D,
        viewport,
        // A thumbnail is a picture of the page, not of the notes somebody
        // stuck to it — and an annotation is the part of a PDF most likely to
        // want to do something other than be looked at.
        annotationMode: pdfjs.AnnotationMode.DISABLE,
      }).promise

      return await toBlob(canvas, THUMBNAIL_QUALITY)
    } finally {
      // Takes the worker down with it. A PDF left open would keep a thread
      // and its whole parsed document alive for as long as the tab is.
      await loading.destroy()
    }
  } catch (err) {
    console.warn('could not render the first page of that PDF', err)
    return null
  }
}

// ---------------------------------------------------------------------------
// A page off the scanner
// ---------------------------------------------------------------------------

/**
 * One captured frame, cropped to `quad` and flattened, ready to upload.
 *
 * `quad` is in fractions of the frame, so the same corners found on the 320px
 * thumbnail the detector works from apply unchanged to the full-resolution
 * still — which is the point of the detector answering in fractions. `null`
 * means nothing was found and the whole frame is kept: a scanner that refuses
 * to take a picture because it cannot see the corners is worse than one that
 * takes an uncropped picture, and the person holding the phone can see
 * perfectly well what is in shot.
 *
 * `originalBytes` is 0, and that is not a missing value: a scan never was a
 * file, so there is no "before" to have shrunk from. The screen's "6.2 MB
 * shrunk to 412 KB" is a true and useful thing to say about a photo off the
 * camera roll and would be an invented one here, so it says the size and
 * stops.
 */
export async function prepareScan(
  frame: ImageBitmap,
  quad: Quad | null,
  originalName: string,
): Promise<PreparedReceipt> {
  const flattened = quad === null ? null : await flattenFrame(frame, quad)
  const source = flattened ?? frame
  try {
    const full = await drawTo(source, LONG_EDGE, WEBP_QUALITY)
    const thumbnail = await drawTo(source, THUMBNAIL_EDGE, THUMBNAIL_QUALITY)
    return {
      kind: 'image',
      file: full,
      contentType: full.type || 'image/webp',
      thumbnail,
      originalName,
      originalBytes: 0,
      storedBytes: full.size + (thumbnail?.size ?? 0),
    }
  } finally {
    if (flattened) release(flattened)
  }
}

/**
 * The frame with `quad` mapped onto the whole of a rectangle.
 *
 * The resampling is a few million bilinear reads in plain JavaScript, which is
 * a fifth of a second on a phone — once, on the shutter, where a fifth of a
 * second reads as the camera having taken the picture. It is not in the worker
 * because the worker is the thing keeping the *viewfinder* smooth, and by the
 * time this runs the viewfinder has already stopped.
 *
 * The output is capped at `LONG_EDGE` here rather than after the fact, so the
 * expensive loop only ever produces pixels that will be kept.
 *
 * The maths is imported here rather than at the top of the file for the same
 * reason pdf.js is: the homography and the edge detector it shares a
 * coordinate system with are the scanner's, and nobody attaching a photo from
 * their camera roll should be made to download a scanner first. By the time
 * this runs the chunk is already in cache — opening the viewfinder fetched it.
 */
async function flattenFrame(frame: ImageBitmap, quad: Quad): Promise<AnyCanvas> {
  const [{ flatten, flattenedSize }, { quadInPixels }] = await Promise.all([
    import('./perspective'),
    import('./receipt-edges'),
  ])

  const read = newCanvas(frame.width, frame.height)
  const reading = read.getContext('2d')
  if (!reading) throw new UnsupportedReceipt('this browser cannot draw to a canvas')
  reading.drawImage(frame, 0, 0)

  const corners = quadInPixels(quad, frame.width, frame.height)
  const size = flattenedSize(corners, LONG_EDGE)
  const flattened = flatten(
    reading.getImageData(0, 0, frame.width, frame.height),
    corners,
    size.width,
    size.height,
  )

  const out = newCanvas(size.width, size.height)
  const writing = out.getContext('2d')
  if (!writing) throw new UnsupportedReceipt('this browser cannot draw to a canvas')
  writing.putImageData(new ImageData(flattened.data, size.width, size.height), 0, 0)

  release(read)
  return out
}

/**
 * Gives a canvas's backing store up now rather than at the next collection.
 *
 * A multi-page scan holds several full-resolution frames at once and each one
 * of these is a few megabytes; on a phone that is the difference between the
 * fourth page working and the tab being reloaded under you.
 */
function release(canvas: AnyCanvas): void {
  canvas.width = 0
  canvas.height = 0
}
